import type { AnalyticsRequestDTO } from '@/api/repository/repositorySchemas'
import { useAnalytics } from '@/hooks/App/useAnalytics'

interface Props {
	appId: string
	/** Filter can include runId as string or string[] for multiple runs */
	filter?: AnalyticsRequestDTO['filter']
	groupBy?: AnalyticsRequestDTO['groupBy']
	populate?: AnalyticsRequestDTO['populate']
	forceRebuild?: boolean
	options?: (typeof useAnalytics)['arguments'][2]
	enabled?: boolean
}

/**
 * Hook to fetch component-level analytics data.
 * Supports filtering by single runId or multiple runIds for aggregated analytics.
 *
 * @example
 * // Single run
 * useComponentData({ appId, filter: { runId: 'run123' }, groupBy: ['ruleType'] })
 *
 * // Multiple runs (aggregated)
 * useComponentData({ appId, filter: { runId: ['run123', 'run456'] }, groupBy: ['ruleType'] })
 */
export const useComponentData = ({
	appId,
	filter,
	groupBy,
	populate,
	forceRebuild,
	options,
	enabled = true,
}: Props) => {
	return useAnalytics(
		'/api/v2/analytics/app/{appId}/components',
		{
			pathParams: {
				appId,
			},
			queryParams: {
				filter,
				groupBy,
				...(populate && { populate }),
				...(forceRebuild && { forceRebuild }),
			},
		},
		{
			enabled: !!appId && !!groupBy && !!filter && enabled,
			refetchOnWindowFocus: false,
			...options,
		}
	)
}
